import { __decorate } from "tslib";
import __NG_CLI_RESOURCE__0 from "./lpr-workflow.component.html?ngResource";
import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import _ from 'lodash';
import { forkJoin, mergeAll, mergeScan, of, Subject, takeUntil, } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { PickListModule } from 'primeng/picklist';
import { TabViewModule } from 'primeng/tabview';
import { LPRManagementService } from 'src/ng-app/features/lpr-management';
import { GridComponent } from 'src/ng-app/components/grid/grid.component';
import { ListComponent } from 'src/ng-app/components/list/list.component';
import { SiteService } from 'src/ng-app/shared/services/site.service';
import { ResourceListService } from 'src/ng-app/shared/services/resource-list.service';
import { OrganizationService } from 'src/ng-app/shared/services/organization.service';
import { Form } from 'formiojs';
import { MessageService } from 'primeng/api';
import { parseApiErrorToMessage } from 'src/ng-app/shared/api-error-parser';
const SHORT_DATE = 'MM/dd/yyyy';
let LprWorkflowComponent = class LprWorkflowComponent {
    messageService;
    LprManagementService = inject(LPRManagementService);
    OrganizationService = inject(OrganizationService);
    SiteService = inject(SiteService);
    ResourceListService = inject(ResourceListService);
    constructor(messageService) {
        this.messageService = messageService;
    }
    #destroy$ = new Subject();
    org = null;
    workflow = null;
    editableWorkflow = null;
    sitesModel = null;
    sitesForOrg = [];
    selectedSites = [];
    availableSites;
    changedSites = new Set();
    resourceListsModel = null;
    resourceListsForOrg = [];
    selectedResourceLists = [];
    changedResourceLists = new Set();
    resourcesModel = [];
    resourcesModelFull = [];
    lprResourceType = null;
    ruleConditionOptions = [
        { label: 'Found', value: 'found' },
        { label: 'Not Found', value: 'notFound' },
    ];
    targetEventNameOptions = [
        { label: 'Matched', value: 'matched' },
        { label: 'Of Interest', value: 'ofInterest' },
    ];
    resourcesGridOptions = {
        showAdd: false,
        showExport: false,
        showColumnsMenu: true,
        actions: false,
        columnMenu: true,
        paginator: true,
        search: true,
        sortable: true,
        contextMenu: false,
    };
    columns = [
        { field: 'data.plate', header: 'Plate', type: 'string' },
        { field: 'data.state', header: 'State', type: 'string' },
        { field: 'data.owner.name', header: 'Owner Name', type: 'string' },
        {
            field: 'createdAt',
            header: 'Created At',
            type: 'date',
            formatOptions: { format: SHORT_DATE },
        },
        {
            field: 'updatedAt',
            header: 'Updated At',
            type: 'date',
            formatOptions: { format: SHORT_DATE },
        }
    ];
    plateMultiSortMeta = [
        {
            field: 'data.plate',
            order: 1
        }
    ];
    plateFilters = {};
    _columnsByField = new Map(_.map(this.columns, col => [col.field, col]));
    ngOnInit() {
        this.LprManagementService.getLprResourceType().subscribe((resourceType) => {
            this.lprResourceType = resourceType;
            this.updateColumns();
        });
        this.LprManagementService.selectedWorkflow$
            .pipe(takeUntil(this.#destroy$))
            .subscribe((workflow) => {
            if (!workflow) {
                return;
            }
            this.editableWorkflow = this.LprManagementService.isEditableWorkflow(workflow)
                ? workflow
                : this.LprManagementService.cloneForEdit(workflow);
            const editOrg = this.editableWorkflow.org;
            this.OrganizationService.getOrganizationById(_.get(editOrg, 'id', editOrg))
                .pipe(takeUntil(this.#destroy$))
                .subscribe((org) => {
                if (org !== this.org) {
                    this.handleOrgChange(org);
                }
            });
            this.setSelectedSites();
            this.setSelectedResourceLists();
        });
    }
    handleOrgChange(org) {
        this.org = org;
        this.SiteService.getSitesForOrg(this.org?.id)
            .pipe(takeUntil(this.#destroy$))
            .subscribe((sites) => {
            this.sitesForOrg = sites;
            this.sitesModel = this.SiteService.model;
            this.setSelectedSites();
        });
        this.LprManagementService.getLprListsForOrg(this.org?.id)
            .pipe(takeUntil(this.#destroy$))
            .subscribe((resourceLists) => {
            this.resourceListsForOrg = resourceLists;
            this.resourceListsModel = this.ResourceListService.model;
            this.setSelectedResourceLists();
        });
    }
    setSelectedSites() {
        this.selectedSites = [];
        this.changedSites.clear();
        const available = new Set(this.sitesForOrg);
        _.forEach(this.editableWorkflow?.sites, (site) => {
            const siteId = _.get(site, 'id', site);
            const siteObj = this.sitesModel?.byId[siteId];
            if (siteObj) {
                available.delete(siteObj);
                this.selectedSites.push(siteObj);
            }
        });
        this.availableSites = [...available];
    }
    setSelectedResourceLists() {
        this.selectedResourceLists = [];
        this.changedResourceLists.clear();
        _.forEach(this.editableWorkflow?.resourcelists, (resourceList) => {
            const resourceListId = _.get(resourceList, 'id', resourceList);
            const resourceListObj = this.resourceListsModel?.byId[resourceListId];
            if (resourceListObj) {
                this.selectedResourceLists.push(resourceListObj);
            }
        });
        this.onResourceListSelectionChange(this.selectedResourceLists);
    }
    onResourceListSelectionChange(selection) {
        this.selectedResourceLists = selection;
        forkJoin(this.selectedResourceLists.map((list) => this.ResourceListService.getResourcesForResourceList(list.id))).pipe(takeUntil(this.#destroy$), mergeAll(), mergeScan((acc, resources) => {
            acc.push(...resources);
            return of(acc);
        }, [])).subscribe((resources) => {
            this.resourcesModelFull = resources;
            this.resourcesModel = resources;
        });
    }
    onResourceListOptionClicked(event) {
        const resourceList = event.option;
        if (this.changedResourceLists.has(resourceList.id)) {
            this.changedResourceLists.delete(resourceList.id);
        }
        else {
            this.changedResourceLists.add(resourceList.id);
        }
        this.editableWorkflow.resourcelists = this.selectedResourceLists;
    }
    async updateColumns() {
        if (!this.lprResourceType?.formSchema) {
            return;
        }
        const formInstance = await new Form(this.lprResourceType.formSchema, {}).ready;
        const columns = [];
        this._columnsByField.clear();
        formInstance.everyComponent((component) => {
            const { hidden, isInputComponent, path, type: componentType, format, label, tree } = component;
            if (!hidden &&
                isInputComponent &&
                !tree &&
                path !== 'data') {
                const type = this.getComponentDataType(componentType);
                const formatOptions = {};
                if (type === 'date') {
                    formatOptions.format = format || SHORT_DATE;
                    if (component.displayInTimezone === 'utc') {
                        formatOptions.timezone = 'UTC';
                    }
                }
                const col = {
                    field: path,
                    header: label,
                    type,
                    ...!_.isEmpty(formatOptions) && { formatOptions },
                };
                columns.push(col);
                this._columnsByField.set(path, col);
            }
        });
        [{
                field: 'createdAt',
                header: 'Created At',
                type: 'date',
                formatOptions: { format: SHORT_DATE },
            },
            {
                field: 'updatedAt',
                header: 'Updated At',
                type: 'date',
                formatOptions: { format: SHORT_DATE },
            }].forEach((dateCol) => {
            columns.push(dateCol);
            this._columnsByField.set(dateCol.field, dateCol);
        });
        this.columns = columns;
    }
    getComponentDataType(type) {
        switch (_.lowerCase(type)) {
            case 'numeric':
            case 'number':
            case 'integer':
            case 'int':
            case 'float':
                return 'numeric';
            case 'checkbox':
                return 'boolean';
            case 'datetime':
            case 'day':
            case 'date':
            case 'time':
                return 'date';
            default:
                return 'text';
        }
    }
    pickListTrackByFn(_index, item) {
        return item.id;
    }
    resetWorkflow() {
        this.editableWorkflow.resetChanges();
        this.setSelectedSites();
        this.setSelectedResourceLists();
    }
    saveWorkflow() {
        if (this.changedSites.size > 0) {
            this.editableWorkflow.sites = this.selectedSites.map(site => site.id);
        }
        if (this.changedResourceLists.size > 0) {
            this.editableWorkflow.resourcelists = this.selectedResourceLists.map(list => list.id);
        }
        this.LprManagementService.saveWorkflow(this.editableWorkflow).subscribe(() => {
            this.resetWorkflow();
            this.messageService.add({
                severity: 'success',
                summary: 'Workflow saved',
                detail: this.editableWorkflow.name,
            });
        }, (err) => {
            console.error('Error saving workflow:', err);
            const errorMessage = parseApiErrorToMessage(err);
            this.messageService.add(errorMessage);
        });
    }
    hasPendingChanges() {
        return this.editableWorkflow.hasChanges() || this.changedSites.size > 0 || this.changedResourceLists.size > 0;
    }
    updateChangedStatus(item) {
        if (this.changedSites.has(item.id)) {
            this.changedSites.delete(item.id);
        }
        else {
            this.changedSites.add(item.id);
        }
        this.editableWorkflow.sites = this.selectedSites;
    }
    onMoveToSource(event) {
        event.items.forEach((item) => this.updateChangedStatus(item));
        this.availableSites.sort((a, b) => a.name.localeCompare(b.name));
    }
    onMoveToTarget(event) {
        event.items.forEach((item) => this.updateChangedStatus(item));
        this.selectedSites.sort((a, b) => a.name.localeCompare(b.name));
    }
    onRuleDropdownChange(prop, event) {
        this.editableWorkflow.rule = {
            ...this.editableWorkflow.rule,
            [prop]: event.value,
        };
    }
    onLazyLoad($event) {
        this.plateFilters = $event.filters;
        this.filterPlates();
        this.plateMultiSortMeta = $event.multiSortMeta;
        this.sortPlates();
    }
    filterPlates() {
        const filters = this.plateFilters;
        if (!filters || Object.keys(filters).length === 0) {
            // If no filters are present, no need to filter.
            // reset to the original list and exit
            this.resourcesModel = this.resourcesModelFull;
            return;
        }
        this.resourcesModel = this.resourcesModelFull.filter((resource) => Object.entries(filters).every(([key, filterValue]) => {
            // If a filter for a key is not an array or is empty, it doesn't restrict this resource for this key.
            if (!Array.isArray(filterValue) || filterValue.length === 0) {
                return true;
            }
            // Check if all conditions within the filter array for this key are met
            // TODO: support for 'or' operator
            return filterValue.every((constraint) => {
                // If constraint value is null or undefined, it means no specific filter is applied for this constraint.
                if (constraint.value === null) {
                    return true;
                }
                const resourceValue = _.get(resource, key);
                // Ensure resourceValue is not null or undefined before applying string or date methods
                if (resourceValue === null) {
                    return false; // Or true, depending on how you want to handle missing data vs. filter
                }
                switch (constraint.matchMode) {
                    case 'startsWith':
                        return String(resourceValue).startsWith(String(constraint.value));
                    case 'endsWith':
                        return String(resourceValue).endsWith(String(constraint.value));
                    case 'contains': // Added for completeness, if applicable
                        return String(resourceValue).includes(String(constraint.value));
                    case 'equals': // Added for completeness, if applicable
                        return resourceValue === constraint.value;
                    case 'dateIs': {
                        // Ensure both values are valid dates before comparing
                        const resourceDate = new Date(resourceValue);
                        const filterDate = new Date(constraint.value);
                        if (isNaN(resourceDate.getTime()) || isNaN(filterDate.getTime())) {
                            return false; // Invalid date comparison
                        }
                        return resourceDate.toDateString() === filterDate.toDateString();
                    }
                    // Add other matchModes as needed
                    default:
                        console.warn(`Unknown matchMode: ${constraint.matchMode}`);
                        return false; // not sure, so don't include
                }
            });
        }));
    }
    sortPlates() {
        this.resourcesModel.sort((a, b) => {
            const sortMeta = this.plateMultiSortMeta;
            for (const meta of sortMeta) {
                const { field, order } = meta;
                const columnType = this._columnsByField.get(field)?.type || 'text';
                let aValue = _.get(a, field);
                let bValue = _.get(b, field);
                if (columnType === 'date') {
                    const aDate = new Date(aValue);
                    const bDate = new Date(bValue);
                    const isAInvalid = isNaN(aDate.getTime());
                    const isBInvalid = isNaN(bDate.getTime());
                    if (isAInvalid && isBInvalid) {
                        // If both are invalid, treat as equal for this sort field
                        // Potentially continue to next sort field or return 0 if it's the last one
                    }
                    else if (isAInvalid) {
                        return order === 1 ? -1 : 1; // Invalid dates first for asc, last for desc
                    }
                    else if (isBInvalid) {
                        return order === 1 ? 1 : -1; // Invalid dates first for asc, last for desc
                    }
                    // If both are valid, proceed with date comparison
                    aValue = aDate;
                    bValue = bDate;
                }
                if (columnType === 'text') {
                    // Convert to strings for comparison
                    const sA = String(aValue || '');
                    const sB = String(bValue || '');
                    const comparison = sA.localeCompare(sB, undefined, { numeric: true });
                    if (comparison !== 0) {
                        return order === 1 ? comparison : -comparison;
                    }
                    // if localeCompare is 0, it means strings are equivalent in current locale
                    // (or one of them was empty string after String(value || '') coercion)
                    // in this case, we don't return immediately but let the loop continue to the next sort meta
                    // or return 0 if this is the last sort meta
                }
                else {
                    // Handling for non-text and non-date types (e.g. numeric)
                    if (aValue < bValue) {
                        return order === 1 ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return order === 1 ? 1 : -1;
                    }
                }
            }
            return 0;
        });
    }
    ngOnDestroy() {
        this.#destroy$.next();
        this.#destroy$.complete();
    }
    static ctorParameters = () => [
        { type: MessageService }
    ];
};
LprWorkflowComponent = __decorate([
    Component({
        selector: 'lpr-workflow',
        template: __NG_CLI_RESOURCE__0,
        standalone: true,
        imports: [
            CommonModule,
            FormsModule,
            ButtonModule,
            DropdownModule,
            InputSwitchModule,
            InputTextModule,
            PickListModule,
            TabViewModule,
            GridComponent,
            ListComponent,
        ],
    })
], LprWorkflowComponent);
export { LprWorkflowComponent };
